import React, { Fragment, useEffect, useState } from 'react';
import { Input, Label } from 'reactstrap';
import IconTicket from '@assets/images/button/icon_button_ticket_inverted.svg';
import { exportExcel, swalMessage } from '@components/common';
import { Button, ExcelButton } from '@components/common/Button';
import PerPageSelect from '@components/common/PerPageSelect';
import { TableHeaderContainerV2 } from '@components/common/Table';
import ToggleSwitch from '@components/common/ToggleSwitch';
import CheckboxGroupFilter from '@components/filter/items/CheckboxGroupFilter';
import DateRangeFilter, {
  DateRangePreset,
} from '@components/filter/items/datepicker/DateRangeFilter';
import InputFilter from '@components/filter/items/InputFilter';
import ToggleFilter from '@components/filter/items/ToggleFilter';
import PageWithFilter from '@components/layout/PageWithFilter';
import { commonWrapperOptions } from '@components/modal/CommonWrapper';
import DetailsModal from '@components/modal/detailsModal';
import TicketInfoV2 from '@components/modal/ticket/TicketInfoV2';
import TicketCustom from '@components/modal/TicketEnrollModal';
import NewTableComponent from '@components/table/NewTableComponent';
import { TicketOverTime, userList } from '@lib/api';
import { blockTicket, forceExitTicket, getAllTicketList } from '@lib/api/v2/ticket';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import useModal from '@lib/hooks/useModal';
import useMousePosition from '@lib/hooks/useMousePosition';
import useAppStore from '@store/useAppStore';
import { media } from '@styles/mediaQuery';
import { asArray, firstArray } from '@util/array';
import Format from '@util/formatter';
import {
  getBlockTooltipMessage,
  TICKET_BUTTON_TEXT,
  TICKET_CONFIRM_MESSAGES,
  TICKET_SUCCESS_MESSAGES,
  TICKET_WARNING_MESSAGES,
} from '@util/ticketMessages';
import styled from 'styled-components';

import { swalReturnWarning } from '@/util/commonFunctions';
import { getSearchAwareDateRange } from '@/util/searchFilters';

export const LabelS = styled(Label)`
  margin-right: 1vw;
  input {
    margin-right: 0.4vw;
    position: static;
    margin-left: 0;
  }
`;
export const InputS = styled(Input).attrs({ required: true })``;

export const InputWithP = styled.span`
  display: flex;
  align-items: center;

  input {
    width: 40%;
    margin-right: 0.2vw;
    margin-top: 0;
    :not(:first-child) {
      margin-left: 0.8vw;
    }
  }
  p {
    margin-bottom: 0;
  }
`;

const ticketTypeOptions = [
  { key: '1', title: '일회권' },
  { key: '2', title: '기간권' },
  { key: '3', title: '시간정액권' },
  { key: '4', title: '세미나실' },
  { key: '5', title: '사물함' },
];

const ticketLevelOptions = [
  { key: '1', title: '1등급' },
  { key: '2', title: '2등급' },
  { key: '3', title: '3등급' },
  { key: '4', title: '4등급' },
];

const ticketUsageOptions = [
  { key: 'unused', title: '사용전' },
  { key: 'using', title: '사용중' },
  { key: 'overtime', title: '시간초과' },
  { key: 'completed', title: '퇴장완료' },
];

const ticketBlockOptions = [
  { key: 'available', title: '사용가능' },
  { key: 'blocked', title: '차단됨' },
];

const allTicketTypes = ticketTypeOptions.map((option) => option.key);
const allTicketLevels = ticketLevelOptions.map((option) => option.key);
const allTicketUsage = ticketUsageOptions.map((option) => option.key);
const allTicketBlock = ticketBlockOptions.map((option) => option.key);

interface ExtendedTicket extends Ticket {
  typeName?: string;
  type1?: React.ReactNode;
  phoneNumtag?: React.ReactNode;
  action?: string | number;
  expireDate2?: string;
  T_isUsing?: React.ReactNode;
  usingTimeKeyword?: string;
  blockKeyword?: string;
  T_block?: React.ReactNode;
  action2?: React.ReactNode;
  outButton?: React.ReactNode;
  remainTimeSec?: number | string;
  ticketBlock?: React.ReactNode;
  ticketBlockKeyword?: string;
  last2?: string;
  isUsingKeyword?: string;
  // 정렬 전용 raw 숫자 필드 (표시값이 포맷 문자열이라 문자열 비교가 깨지는 컬럼용)
  remainTimeSort?: number;
  expireDateSort?: number;
  lastInSort?: number;
}

const TicketInfo = () => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();
  const { openModal } = useModal();
  const { merchantData } = useAppStore();
  const merchantId = merchantData?.id;

  const mousePosition = useMousePosition();
  const [lists, setLists] = useState<ExtendedTicket[]>([]);

  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'year').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [isShowOverTime, setIsShowOverTime] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const [ticketTypes, setTicketTypes] = useState(allTicketTypes);
  const [ticketLevels, setTicketLevels] = useState(allTicketLevels);
  const [usageStatus, setUsageStatus] = useState(allTicketUsage);
  const [blockStatus, setBlockStatus] = useState(allTicketBlock);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [isCustom, setIsCustom] = useState(false);
  const [detailUserModal, setDetailUserModal] = useState(false);
  const [userData, setUserData] = useState({});

  const [tooltip, setTooltip] = useState({
    enabled: false,
    message: '',
  });

  // 페이지네이션만 즉시 적용 (초과결제는 filterValues에 포함되어 throttle 적용됨)
  useEffect(() => {
    getUserTicketListApi();
  }, [currentPage, pageSize]);

  const handleSetLists = (item: ExtendedTicket) => {
    openModal(
      () => (
        <TicketInfoV2
          ticketId={item.id}
          account="merchant"
          refetch={() => getUserTicketListApi()}
        />
      ),
      commonWrapperOptions,
    );
  };

  const sendDetailData = (phoneNum: string | null) => {
    if (phoneNum) getUserList(phoneNum);
  };

  const getUserList = async (phoneNum: string) => {
    if (!merchantId) return;
    try {
      const result = await createRequest(userList, merchantId, phoneNum);
      if (result && result.status === 200) {
        setUserData(firstArray(result?.data)[0] || {});
        setDetailUserModal(!detailUserModal);
      }
    } catch (error) {
      catchError(error);
    }
  };

  const showDetailModal = () => {
    setDetailUserModal(!detailUserModal);
  };

  const getUserTicketListApi = async () => {
    try {
      const searchAwareDateRange = getSearchAwareDateRange({
        startDate,
        endDate,
        hasSearch: Boolean(phoneNumber.trim() || ticketId.trim()),
        today: dayjs().format('YYYY-MM-DD'),
      });
      const queryStartDate = isShowOverTime
        ? dayjs().subtract(1, 'year').format('YYYY-MM-DD')
        : dayjs(searchAwareDateRange.startDate).format('YYYY-MM-DD');
      const queryEndDate = isShowOverTime
        ? dayjs().format('YYYY-MM-DD')
        : dayjs(searchAwareDateRange.endDate).format('YYYY-MM-DD');

      const data = await createRequest(
        getAllTicketList,
        queryStartDate,
        queryEndDate,
        currentPage,
        pageSize,
        {
          isOvertime: isShowOverTime,
          activeOnly,
          ticketTypes,
          ticketLevels,
          usageStatus,
          blockStatus,
          phoneNumber: phoneNumber || undefined,
          ticketId: ticketId || undefined,
        },
      );

      if (data.status === 200) {
        const tickets = asArray<ExtendedTicket>(data.data?.tickets);
        setTotalCount(Number(data.data?.totalCount) || tickets.length);
        const processedList = tickets.map((item: ExtendedTicket, idx: number) => {
          item.typeName =
            item.type === 1 && item.seatNumber
              ? ` 일회권 (${item.seatNumber}번)`
              : item.type === 1 && !item.seatNumber
                ? ' 일회권'
                : item.type === 4 && item.seatNumber
                  ? ` 세미나실 (${item.seatNumber}번)`
                  : item.type === 4 && !item.seatNumber
                    ? ' 세미나실'
                    : item.type === 5 && item.seatNumber
                      ? ` 사물함 (${item.seatNumber}번)`
                      : item.type === 5 && !item.seatNumber
                        ? ' 사물함'
                        : item.type === 2
                          ? ` 기간권 - ${item.seatType === 0 ? '자유석' : '고정석'}`
                          : item.type === 3
                            ? '시간정액권'
                            : '사물함';

          item.type1 =
            item.type === 1 ? (
              <p style={{ color: '#EBAF22' }}>
                일회권
                {item.seatNumber ? `(${item.seatNumber}번)` : ''}
              </p>
            ) : item.type === 2 ? (
              <>
                {item.seatType === 0 ? (
                  <p style={{ color: '#E03D64' }}>기간권 - 자유석</p>
                ) : (
                  <p style={{ color: '#1BBABC' }}>기간권 - 고정석</p>
                )}
              </>
            ) : item.type === 3 ? (
              <p style={{ color: '#33946B' }}>시간정액권</p>
            ) : item.type === 4 ? (
              <p style={{ color: '#3E67B6' }}>
                세미나실
                {item.seatNumber ? `(${item.seatNumber}번)` : ''}
              </p>
            ) : (
              <p style={{ color: '#AB84FF' }}>
                사물함
                {item.seatNumber ? `(${item.seatNumber}번)` : ''}
              </p>
            );

          item.phoneNumtag = (
            <div
              onClick={() => {
                sendDetailData(item.phoneNum);
              }}
              className="phoneNum-click">
              {Format.formatPhone(item.phoneNum)}
            </div>
          );

          // 정렬키는 포맷 변환 전 원본 시각으로 산출 (빈 값은 0 → 가장 과거로 정렬)
          item.lastInSort = item.lastIn ? dayjs(item.lastIn).valueOf() || 0 : 0;
          item.lastIn = item.lastIn ? dayjs(item.lastIn).format('YYYY-MM-DD HH:mm:ss') : '';
          item.startDate = item.startDate
            ? dayjs(item.startDate).format('YYYY-MM-DD HH:mm:ss')
            : '';

          const t1 = dayjs(item.endDate);
          const t3 = dayjs(new Date());

          item.action =
            +item.type === 2 && +item.seatType === 0
              ? Format.formatSecondsToHHmmss(item.remainTime)
              : Format.formatSecondsToHHmmss(item.remainTime + (item.usedTime || 0));

          item.expireDate2 =
            (item.type === 3 || item.type === 5) &&
            Math.ceil(dayjs.duration(dayjs(t1).diff(t3)).asDays()) > 0
              ? `${Math.ceil(dayjs.duration(dayjs(t1).diff(t3)).asDays())}일`
              : '-';

          // 유효기간 정렬키: 만료일(endDate) 타임스탬프 (표시값 "N일"/"-" 문자열 비교 회피)
          item.expireDateSort = item.endDate ? dayjs(item.endDate).valueOf() || 0 : 0;

          item.T_isUsing =
            item.isUsing === 1 ? (
              <div>사용중</div>
            ) : item.isUsing === 2 ? (
              <div style={{ color: '#F04E36' }}>시간 초과</div>
            ) : item.lastOut ? (
              <div style={{ color: '#53852A' }}>퇴장완료</div>
            ) : (
              <div>사용전</div>
            );

          item.usingTimeKeyword =
            item.isUsing === 1
              ? '사용중'
              : item.isUsing === 2
                ? '시간초과'
                : item.lastOut
                  ? '퇴장완료'
                  : '사용전';

          item.blockKeyword = item.block === 1 ? '티켓사용불가' : '티켓사용가능';
          item.T_block =
            item.block === 1 ? (
              <p style={{ color: '#F04E36' }}>티켓사용불가</p>
            ) : (
              <p>티켓사용가능</p>
            );
          item.action2 = (
            <button
              className="btn-primary btn-sm"
              id={idx.toString()}
              onClick={(e) => handleSetLists(item)}>
              자세히
            </button>
          );
          item.outButton =
            item.isUsing === 1 ? (
              <Fragment>
                <button className="btn-secondary btn-sm" onClick={() => outTicket(item)}>
                  강제퇴장처리
                </button>
                {item.seatType === 1 && (
                  <button
                    className="btn-secondary btn-sm"
                    style={{ marginLeft: '4px' }}
                    onClick={() => outFullTicket(item)}>
                    {TICKET_BUTTON_TEXT.FULL_EXIT}
                  </button>
                )}
              </Fragment>
            ) : item.isUsing === 2 ? (
              <button className="btn-success btn-sm" onClick={() => additional(item)}>
                초과결제해제
              </button>
            ) : (
              <button disabled className="btn-sm">
                퇴장처리
              </button>
            );

          item.last2 = item.lastOut ? dayjs(item.lastOut).format('YYYY-MM-DD HH:mm:ss') : '';
          item.isUsingKeyword =
            item.isUsing === 1 ? '강제퇴장처리' : item.isUsing === 2 ? '초과결제해제' : '퇴장처리';
          const nowTime = dayjs();
          const endTime = dayjs(item.endDate);
          const today_diff = dayjs.duration(endTime.diff(nowTime)).asMilliseconds();
          item.remainTimeSec = Format.calculateTicketRemainTime(item);
          // 잔여시간 정렬키: 표시값이 "HH:mm:ss"/"-" 포맷 문자열이라 raw 초(remainTime)로 정렬
          item.remainTimeSort = Number(item.remainTime) || 0;

          const isExpired = today_diff <= 0;

          item.ticketBlock =
            (today_diff > 0 && +item.block === 0 && item.remainTime > 0) ||
            (item.type === 5 && today_diff > 0 && item.block !== 1) ? (
              <ToggleSwitch checked={item.block === 0} onChange={() => blockApiFunc(item)} />
            ) : today_diff > 0 &&
              +item.block === 1 &&
              item.remainTime > 0 &&
              (item.type === 2 || item.type === 3) ? (
              <ToggleSwitch checked={item.block === 0} onChange={() => blockApiFunc(item)} />
            ) : (
              <div
                onMouseEnter={() => {
                  setTooltip({
                    enabled: true,
                    message: getBlockTooltipMessage(item, isExpired),
                  });
                }}
                onMouseLeave={() => setTooltip({ ...tooltip, enabled: false })}>
                <ToggleSwitch checked={false} onChange={() => {}} disabled />
              </div>
            );

          item.ticketBlockKeyword =
            today_diff > 0 && +item.block === 0 && item.remainTime > 0
              ? '입장권 차단'
              : today_diff > 0 && +item.block === 1 && item.remainTime > 0
                ? '입장권 허용'
                : '입장권 차단';

          return item;
        });

        setLists(processedList);
      }
    } catch (error) {
      catchError(error);
    }
  };

  const blockApiFunc = async (item: ExtendedTicket) => {
    const isBlocking = item.block === 0;
    const message = isBlocking ? TICKET_CONFIRM_MESSAGES.BLOCK : TICKET_CONFIRM_MESSAGES.UNBLOCK;
    swalMessage(
      'warning',
      message,
      isBlocking ? TICKET_BUTTON_TEXT.BLOCK : TICKET_BUTTON_TEXT.UNBLOCK,
      true,
      () => handleBlockTicket(item.id, isBlocking),
      undefined,
      TICKET_BUTTON_TEXT.CANCEL,
    );
  };

  const outTicket = async (e: ExtendedTicket) => {
    const nowTime = dayjs();
    const diffTime = dayjs.duration(nowTime.diff(e.endDate)).asMilliseconds();
    if (diffTime > 0) return swalReturnWarning(TICKET_WARNING_MESSAGES.EXPIRED_TICKET);
    swalMessage(
      'warning',
      TICKET_CONFIRM_MESSAGES.FORCE_EXIT,
      TICKET_BUTTON_TEXT.EXIT,
      true,
      () => handleForceExit(e.id),
      undefined,
      TICKET_BUTTON_TEXT.CANCEL,
    );
  };

  // LETMEUP-619: 고정석 기간권 전용 "완전퇴장" — outTicket/handleForceExit(일반 퇴장)은
  // 고정석의 tb_seat.status 를 rest 로만 바꾸고 idle 로 완전히 비우지 않는다(계약 유지 목적).
  // 이 버튼은 이용권 계약을 종료(block=1)하며 좌석을 즉시 다른 회원에게 개방한다.
  const outFullTicket = async (e: ExtendedTicket) => {
    const nowTime = dayjs();
    const diffTime = dayjs.duration(nowTime.diff(e.endDate)).asMilliseconds();
    if (diffTime > 0) return swalReturnWarning(TICKET_WARNING_MESSAGES.EXPIRED_TICKET);
    swalMessage(
      'warning',
      TICKET_CONFIRM_MESSAGES.FULL_EXIT,
      TICKET_BUTTON_TEXT.FULL_EXIT,
      true,
      () => handleFullExit(e.id),
      undefined,
      TICKET_BUTTON_TEXT.CANCEL,
    );
  };

  const additional = async (ticket: ExtendedTicket) => {
    swalMessage(
      'warning',
      TICKET_CONFIRM_MESSAGES.OVERTIME_CANCEL,
      TICKET_BUTTON_TEXT.OVERTIME_CANCEL,
      true,
      () => handleTicketOverTime(ticket.merchantId, ticket.id),
      undefined,
      TICKET_BUTTON_TEXT.CANCEL,
    );
  };

  const handleBlockTicket = async (ticketId: number, isBlocking: boolean) => {
    try {
      await createRequest(blockTicket, {
        ticketId,
        block: isBlocking ? 1 : 0,
      });
      swalMessage(
        'success',
        isBlocking ? TICKET_SUCCESS_MESSAGES.BLOCK : TICKET_SUCCESS_MESSAGES.UNBLOCK,
      );
      getUserTicketListApi();
    } catch (error) {
      catchError(error, '입장권 차단/허용 중 오류가 발생했습니다.');
    }
  };

  const handleTicketOverTime = async (merchantId: number, ticketId: number) => {
    try {
      await createRequest(TicketOverTime, {
        merchantId,
        ticketId,
      });
      swalMessage('success', TICKET_SUCCESS_MESSAGES.OVERTIME_CANCEL);
      getUserTicketListApi();
    } catch (error) {
      catchError(error);
    }
  };

  const handleForceExit = async (ticketId: number) => {
    try {
      await createRequest(forceExitTicket, { ticketId });
      swalMessage('success', TICKET_SUCCESS_MESSAGES.FORCE_EXIT);
      getUserTicketListApi();
    } catch (error) {
      catchError(error, '퇴장처리 중 에러가 발생했습니다.');
    }
  };

  const handleFullExit = async (ticketId: number) => {
    try {
      await createRequest(blockTicket, { ticketId, block: 1 });
      swalMessage('success', TICKET_SUCCESS_MESSAGES.FULL_EXIT);
      getUserTicketListApi();
    } catch (error) {
      catchError(error, '완전퇴장 처리 중 에러가 발생했습니다.');
    }
  };

  const close = () => {
    setIsCustom(!isCustom);
  };

  const handleApplyFilter = () => {
    getUserTicketListApi();
  };

  const handleResetFilter = () => {
    const defaultStart = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
    const defaultEnd = dayjs().format('YYYY-MM-DD');
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setPhoneNumber('');
    setTicketId('');
    setIsShowOverTime(false);
    setActiveOnly(false);
    setTicketTypes(allTicketTypes);
    setTicketLevels(allTicketLevels);
    setUsageStatus(allTicketUsage);
    setBlockStatus(allTicketBlock);
    setCurrentPage(1);
  };

  const handleDateChange = (event: any) => {
    if (event.target.name === 'startDate') {
      setStartDate(event.target.value);
    } else if (event.target.name === 'endDate') {
      setEndDate(event.target.value);
    }
    // setCurrentPage(1) 제거 - enableAutoRefetch가 처리함
  };

  const handleActiveOnlyChange = (enabled: boolean) => {
    setActiveOnly(enabled);
    if (enabled) setIsShowOverTime(false);
    setCurrentPage(1);
  };

  const handleOverTimeChange = (enabled: boolean) => {
    setIsShowOverTime(enabled);
    if (enabled) setActiveOnly(false);
    setCurrentPage(1);
  };

  const columns = [
    { Header: '권번', accessor: 'id', width: '5.5rem', isSort: true },
    { Header: '유형', accessor: 'type1', width: '9rem' },
    { Header: '전화번호', accessor: 'phoneNumtag', width: '9rem', stopRowClick: true },
    { Header: '등급', accessor: 'level', width: '4.5rem', isSort: true },
    { Header: '사용가능 여부', accessor: 'T_block', width: '8rem' },
    { Header: '사용 상태', accessor: 'T_isUsing', width: '6.5rem' },
    {
      Header: '잔여시간',
      accessor: 'remainTimeSec',
      width: '8rem',
      isSort: true,
      sortKey: 'remainTimeSort',
    },
    {
      Header: '유효기간',
      accessor: 'expireDate2',
      width: '6rem',
      isSort: true,
      sortKey: 'expireDateSort',
    },
    {
      Header: '최근입장',
      accessor: 'lastIn',
      width: '11.5rem',
      isSort: true,
      sortKey: 'lastInSort',
    },
    { Header: '퇴장처리', accessor: 'outButton', width: '8.5rem', stopRowClick: true },
    { Header: '입장권차단', accessor: 'ticketBlock', width: '6.5rem', stopRowClick: true },
    { Header: '자세히', accessor: 'action2', width: '6rem', stopRowClick: true },
  ];

  return (
    <Fragment>
      <Tooltip enabled={tooltip.enabled} x={mousePosition.x} y={mousePosition.y}>
        {tooltip.message}
      </Tooltip>
      {detailUserModal && (
        <DetailsModal
          isOpen={detailUserModal}
          close={showDetailModal}
          seat={userData}
          back={true}
        />
      )}

      {isCustom && (
        <TicketCustom mid={merchantId} close={close} reloading={() => getUserTicketListApi()} />
      )}

      <PageWithFilter
        title="입장권 정보 조회"
        filters={[
          <DateRangeFilter
            key="date-range"
            title="조회 기간"
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            defaultPreset={DateRangePreset.THIS_WEEK}
            disabled={isShowOverTime || activeOnly}
            description={
              isShowOverTime
                ? '초과결제 조회 시 전체 기간으로 고정됩니다.'
                : activeOnly
                  ? '활성 이용권은 전체 기간에서 조회됩니다.'
                  : undefined
            }
          />,
          <CheckboxGroupFilter
            key="ticket-type"
            title="이용권 종류"
            options={ticketTypeOptions}
            selected={ticketTypes}
            setSelected={setTicketTypes}
            initialValue={allTicketTypes}
          />,
          <CheckboxGroupFilter
            key="ticket-level"
            title="등급"
            options={ticketLevelOptions}
            selected={ticketLevels}
            setSelected={setTicketLevels}
            initialValue={allTicketLevels}
          />,
          <CheckboxGroupFilter
            key="ticket-usage"
            title="사용 상태"
            options={ticketUsageOptions}
            selected={usageStatus}
            setSelected={setUsageStatus}
            initialValue={allTicketUsage}
          />,
          <CheckboxGroupFilter
            key="ticket-block"
            title="차단 여부"
            options={ticketBlockOptions}
            selected={blockStatus}
            setSelected={setBlockStatus}
            initialValue={allTicketBlock}
          />,
          <ToggleFilter
            key="active-ticket-filter"
            title="활성 이용권만"
            enabled={activeOnly}
            onChange={handleActiveOnlyChange}
            description={
              activeOnly
                ? '전체 기간에서 만료되지 않고 차단되지 않은 이용권만 조회합니다.'
                : '활성화하면 조회 기간과 관계없이 현재 유효한 이용권만 볼 수 있습니다.'
            }
          />,
          <ToggleFilter
            key="overtime-filter"
            title="초과결제 확인"
            enabled={isShowOverTime}
            onChange={handleOverTimeChange}
            description={
              isShowOverTime
                ? '초과결제 이용권 조회 시 전체 기간 조회로 고정됩니다.'
                : '활성화하면 초과결제가 필요한 이용권만 볼 수 있습니다.'
            }
          />,
          <InputFilter
            key="search-phone"
            title="전화번호"
            input={phoneNumber}
            setInput={setPhoneNumber}
            placeholder="전화번호를 입력하세요"
          />,
          <InputFilter
            key="search-ticket-id"
            title="권번"
            input={ticketId}
            setInput={setTicketId}
            placeholder="권번을 입력하세요"
          />,
        ]}
        mobileQuickFilters={
          [
            <InputFilter
              key="mobile-search-phone"
              title="전화번호"
              input={phoneNumber}
              setInput={setPhoneNumber}
              placeholder="전화번호 검색"
            />,
            <ToggleFilter
              key="mobile-active-ticket-filter"
              title="활성 이용권만"
              enabled={activeOnly}
              onChange={handleActiveOnlyChange}
            />,
          ]
        }
        refetchOnFilterChange
        filterDependencies={[
          startDate,
          endDate,
          isShowOverTime,
          activeOnly,
          ticketTypes,
          ticketLevels,
          usageStatus,
          blockStatus,
          phoneNumber,
          ticketId,
        ]}
        onApplyFilter={handleApplyFilter}
        onResetFilter={handleResetFilter}>
        <TicketTableHeader>
          <PerPageSelect
            value={pageSize}
            onChange={(value) => {
              setPageSize(value);
              setCurrentPage(1);
            }}
          />
          <div className="ticket-actions">
            <ExcelButton
              onClick={() => exportExcel(lists, startDate, endDate, '입장권 정보 조회')}
            />
            <Button
              icon={IconTicket}
              keepLabelOnMobile
              onClick={() => setIsCustom(true)}
              buttonType="action">
              입장권 발행
            </Button>
          </div>
        </TicketTableHeader>

        <NewTableComponent
          columns={columns}
          data={lists}
          defaultData={lists}
          notFoundText="조회된 입장권이 없어요."
          clickRowEvent={(item) => handleSetLists(item)}
          pagination
          postsPerPage={pageSize}
          paginationConfig={{
            currentPage,
            setCurrentPage,
            totalcount: totalCount,
            postsPerPage: pageSize,
          }}
          viewPreferenceKey="ticket-list-view"
        />
      </PageWithFilter>
    </Fragment>
  );
};

export default TicketInfo;

const Tooltip = styled.div<{ enabled: boolean; x: number; y: number }>`
  display: ${({ enabled }) => (enabled ? 'inherit' : 'none')};
  position: fixed;

  left: ${({ x }) => `${x + 10}px`};
  top: ${({ y }) => `${y + 10}px`};

  background: white;
  padding: 1rem;
  border-radius: 0.25rem;
  z-index: 10;

  word-break: keep-all;

  max-width: 10rem;

  box-shadow: 0px 3px 12px rgba(89, 102, 122, 0.15);
`;

const TicketTableHeader = styled(TableHeaderContainerV2)`
  justify-content: space-between;

  .ticket-actions {
    display: flex;
    gap: ${({ theme }) => theme.spacing.sm};
  }

  ${media.tablet`
    width: 100%;
    flex-wrap: nowrap;
    gap: ${({ theme }) => theme.spacing.sm};

    > div:first-child {
      width: auto;
      flex: 0 0 auto;
    }

    select {
      width: auto;
      min-width: 7.75rem;
      min-height: ${({ theme }) => theme.control.heightSm};
      border-color: ${({ theme }) => theme.neutralGray[300]};
      border-radius: ${({ theme }) => theme.radius.sm};
      background-color: ${({ theme }) => theme.white};
    }

    .ticket-actions {
      flex: 1;
      justify-content: flex-end;
    }
  `}
`;
